<template>
  <div class="page">
    <div class="title" v-show="gltfFile.size">
      <div class="name">
        {{ gltfFile.name }}
      </div>
      <div class="file-size">文件大小：{{ gltfFile.size }}MB</div>
    </div>

    <div class="loading">
      <div>
        <i class="el-icon-loading" v-if="loading"></i>
        <span :class="{ 'fail-color': isFail }">{{ loadingText }}</span>
      </div>
    </div>
  </div>
</template>
<script>
import pako from 'pako';
import axios from 'axios';
import { getUrlStringQueryParam } from '@/utils/common.js';

import * as THREE from 'three';
import { GLTFLoader } from './js/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { Data } from 'js/class/Data';

import FileService from 'js/fileService/index.js';

const requestUrl = 'https://bim.geto.pro';
// const requestUrl = 'http://192.168.64.253:8585';

const gltfLoader = new GLTFLoader();
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath(`/draco/`);
dracoLoader.setDecoderConfig({ type: 'js' });
dracoLoader.preload();
gltfLoader.setDRACOLoader(dracoLoader);

let group = new THREE.Group();

export default {
  name: 'Pretreatment',
  data() {
    return {
      isFail: false,
      loadingText: '模型处理中...',
      loading: true,
      propertiesJson: {},
      totalLength: 0,
      totalModel: 0,
      current: 0,
      gltfFile: {
        name: '',
        size: '',
      },
    };
  },
  created() {},
  async mounted() {
    const id = this.getQueryString('id');
    await this.createCache(id);
    await this.updateCacheStatus(id);
    this.loadingText = '创建模型缓存完成';
    this.loading = false;
  },
  methods: {
    async updateCacheStatus(id) {
      const result = await new Data.getInstance().get({
        url: `${requestUrl}/api/buildedcache/finish/${id}`,
        method: 'GET',
      });
    },
    async createCache(modelId, typeKey) {
      let infoGltf = {
        gltfFineUrl: 1,
        gltfMediumUrl: 2,
      };

      if (typeKey) {
        const key = 1 ? 'gltfFineUrl' : 'gltfMediumUrl';
        delete infoGltf[key];
      }

      let index = 0;
      for (const [name, type] of Object.entries(infoGltf)) {
        const info = await this.getFilePath(modelId);

        this.gltfFile = {
          name: info.rvtName,
          size: (info.rvtSize / 1024 / 1024).toFixed(2),
        };

        await this.getPropertiesJson(info.propertyUrl);

        const len = Object.values(this.propertiesJson).length;

        const { meshesAll, meshesUniqueId } = await this.initMeshes(info[name]);
        if (len) {
          const rangeId = {};

          for (const [id, code] of Object.entries(this.propertiesJson)) {
            index += 1;
            const targetMesh = meshesUniqueId[id];
            if (!targetMesh || rangeId[id]) {
              continue;
            }
            const codeArr = [];
            const targetBox = new THREE.Box3().setFromObject(targetMesh);
            targetBox.expandByScalar(800);
            const rangeBox = new THREE.Box3().setFromObject(targetMesh);
            rangeBox.expandByScalar(200);

            for (const mesh of meshesAll) {
              if (
                rangeBox.intersectsBox(new THREE.Box3().setFromObject(mesh))
              ) {
                rangeId[mesh.userData.UniqueId] = code;
                if (this.propertiesJson[mesh.userData.UniqueId]) {
                  codeArr.push(this.propertiesJson[mesh.userData.UniqueId]);
                }
              }

              if (
                targetBox.intersectsBox(new THREE.Box3().setFromObject(mesh))
              ) {
                const matrix = mesh.parent
                  ? mesh.parent.matrixWorld
                  : mesh.matrixWorld;
                mesh.applyMatrix4(matrix);
                mesh.material = mesh.material.clone();
                group.add(mesh);
              }
            }

            const box = new THREE.Box3().setFromObject(group);
            const center = box.getCenter(new THREE.Vector3());
            const vector = box.getSize(new THREE.Vector3());
            group.position.x += group.position.x - center.x;
            group.position.y += group.position.y - center.y;
            group.position.z += group.position.z - center.z;
            const boxCenter = {
              cameraSize: vector.length(),
              groupPostion: group.position,
            };
            let groupJson = JSON.stringify(group.toJSON());
            const codeNew = [...new Set(codeArr)].join(',');

            const file = this.createJsonFile(code, groupJson);
            const fileId = await this.saveFileJson(file);
            await this.saveInstanceMeshes(
              modelId,
              fileId,
              boxCenter,
              codeNew,
              type
            );

            groupJson = null;
            let pro = ((index / this.totalLength) * 100).toFixed(2);
            this.loadingText = `创建模型缓存中...${pro}%`;
          }
        } else {
          this.loadingText = '创建模型缓存中...100%';
        }
      }
    },
    createJsonFile(name, content) {
      const blobGzip = this.jsonCompress(content);
      const blob = new Blob([blobGzip], {
        type: 'application/json',
      });

      const file = new File([blob], `${name}.json`, {
        type: 'application/json',
      });
      return file;
    },
    jsonCompress(groupJson) {
      const uint8Array = new TextEncoder().encode(groupJson);
      const compressedData = pako.gzip(uint8Array);

      // 将Uint8Array分批转换为字符串
      const CHUNK_SIZE = 0x8000;
      let str = '';
      for (let i = 0; i < compressedData.length; i += CHUNK_SIZE) {
        const chunk = compressedData.subarray(i, i + CHUNK_SIZE);
        str += String.fromCharCode.apply(null, chunk);
      }

      // 将字符串转换为Base64编码的字符串
      const base64Str = btoa(str);
      return base64Str;
    },
    async saveFileJson(file) {
      try {
        const fileService = new FileService();
        const { success } = await fileService.startUploadSingleFile(file);
        if (!success) {
          throw new Error('');
        }
        const fileInfo = fileService.getFileInfo();
        return fileInfo.fileID;
        // this.onFileSuccess(fileInfo, fileObj);
      } catch (error) {
        this.errorTips('创建模型缓存失败，请刷新页面重试');
        throw new Error(error);
      }
    },
    async getFilePath(id) {
      if (!id) {
        this.errorTips('请设置要创建缓存的模型ID');
      }
      const res = await new Data.getInstance().get({
        url: `${requestUrl}/api/info/${id}`,
        method: 'GET',
      });
      if (res.data.code == 200) {
        return res.data;
      } else {
        this.errorTips('模型ID不存在');
      }
    },
    async getPropertiesJson(url) {
      const res = await new Data.getInstance().getJson(url);
      if (Object.values(res.data).length) {
        for (const [index, item] of Object.entries(res.data)) {
          if (item['文字'] && item['文字']['铝模板位置编号']) {
            const code = item['文字']['铝模板位置编号'];
            if (code != '0') {
              this.propertiesJson[index] = code;
            }
          }
        }
        this.totalLength = Object.values(this.propertiesJson).length * 2;
      }
    },
    loadGltf(path) {
      return new Promise((resolve, reject) => {
        gltfLoader.load(
          path,
          (gltf) => {
            resolve(gltf);
          },
          (xhr) => {},
          (error) => {}
        );
      });
    },
    async initMeshes(filePaths) {
      const meshesAll = [];
      const meshesUniqueId = {};
      const promises = filePaths.map((url) => this.loadGltf(url));

      const parts = await Promise.all(promises);
      const processMeshes = (child) => {
        child.updateMatrixWorld();
        if (child.isMesh) {
          const userData = this.getUniqueId(child);
          child.userData = {
            ...userData,
            code: this.propertiesJson[userData.UniqueId],
          };
          if (child.userData.UniqueId) {
            meshesUniqueId[child.userData.UniqueId] = child;
          }
          meshesAll.push(child);
        }
      };

      for (let part of parts) {
        part.scene.traverse(processMeshes);
      }
      return { meshesAll, meshesUniqueId };
    },
    async saveInstanceMeshes(id, fileId, boxCenter, code_new, type) {
      try {
        await new Promise((resolve, reject) => {
          axios({
            method: 'post',
            url: `${requestUrl}/api/BuildedCache/store`,
            dataType: 'json',
            crossDomain: true,
            cache: false,
            data: {
              id,
              file_id: fileId,
              code_new,
              type,
              box_center: JSON.stringify(boxCenter),
            },
          })
            .then((result) => {
              group = new THREE.Group();
              resolve(true);
            })
            .catch((error) => {
              reject(error);
            });
        });
      } catch (error) {
        this.errorTips('创建模型缓存失败，请刷新页面重试');
        throw new Error(error);
      }
    },
    getUniqueId(mesh) {
      const { userData } = mesh;
      if (mesh.userData.UniqueId) {
        return { ...userData };
      } else {
        return this.getUniqueId(mesh.parent);
      }
    },
    getQueryString(field) {
      return getUrlStringQueryParam(window.location.href, field);
    },
    errorTips(str) {
      this.isFail = true;
      this.loadingText = str;
      this.loading = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.page {
  width: 100%;
  height: 100%;
}
.loading {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #409eff;
  text-align: center;
  line-height: 40px;
}
.fail-color {
  color: #f56c6c;
}

.title {
  min-height: 70px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .name {
    font-weight: bold;
    font-size: 18px;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .file-size {
    font-size: 12px;
    color: #3a3a3a;
  }
}
</style>
